import { useState } from 'react';
import { BookOpen, Globe, Plus, Palette, MoveVertical, Shield } from 'lucide-react';

interface GuestSetupProps {
  onMemberLogin: () => void;
}

export default function GuestSetup({ onMemberLogin }: GuestSetupProps) {
  const [language, setLanguage] = useState<'ja' | 'en'>('ja');

  const content = {
    ja: {
      title: 'SNS Launcher の使い方',
      subtitle: 'アプリの使い方を学ぶ',
      memberLogin: '研究参加者の方はこちら',
      memberLoginButton: 'メンバーログイン',
      sections: [
        {
          icon: BookOpen,
          title: 'デフォルトアプリ',
          description: '4つの人気SNSにワンタップでアクセス',
          items: [
            'YouTube - 動画を見る',
            'Instagram - 写真を共有',
            'Facebook - 友達とつながる',
            'X - ニュースや話題をチェック'
          ]
        },
        {
          icon: Plus,
          title: 'カスタムショートカットを追加',
          description: 'お気に入りのウェブサイトを追加できます',
          items: [
            '設定ボタンをタップ',
            'アプリ名とURLを入力',
            '色とアイコンを選択',
            '追加ボタンをタップ'
          ]
        },
        {
          icon: MoveVertical,
          title: 'アプリを並べ替え',
          description: 'ドラッグ＆ドロップで簡単に並べ替え',
          items: [
            '設定画面を開く',
            'アプリをドラッグして移動',
            '好きな順序に並べ替え',
            '変更は自動的に保存されます'
          ]
        },
        {
          icon: Palette,
          title: '色とアイコンをカスタマイズ',
          description: '自分好みのデザインに変更',
          items: [
            '設定画面で編集アイコンをタップ',
            'カラーパレットから色を選択',
            'アイコンライブラリから選択',
            '保存して適用'
          ]
        }
      ]
    },
    en: {
      title: 'How to Use SNS Launcher',
      subtitle: 'Learn how to use the app',
      memberLogin: 'For research participants',
      memberLoginButton: 'Member Login',
      sections: [
        {
          icon: BookOpen,
          title: 'Default Apps',
          description: 'One-tap access to 4 popular social media platforms',
          items: [
            'YouTube - Watch videos',
            'Instagram - Share photos',
            'Facebook - Connect with friends',
            'X - Check news and trends'
          ]
        },
        {
          icon: Plus,
          title: 'Add Custom Shortcuts',
          description: 'Add your favorite websites',
          items: [
            'Tap the settings button',
            'Enter app name and URL',
            'Select color and icon',
            'Tap add button'
          ]
        },
        {
          icon: MoveVertical,
          title: 'Reorder Apps',
          description: 'Easy drag and drop reordering',
          items: [
            'Open settings screen',
            'Drag apps to move',
            'Arrange in your preferred order',
            'Changes save automatically'
          ]
        },
        {
          icon: Palette,
          title: 'Customize Colors & Icons',
          description: 'Personalize your design',
          items: [
            'Tap edit icon in settings',
            'Choose color from palette',
            'Select from icon library',
            'Save and apply'
          ]
        }
      ]
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header with Language Toggle */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/app_icon.png"
              alt="SNS Launcher"
              className="w-10 h-10 rounded-full shadow-sm"
            />
            <h1 className="text-xl font-bold text-gray-800">SNS Launcher</h1>
          </div>

          {/* Language Toggle */}
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${language === 'ja' ? 'text-blue-600' : 'text-gray-500'}`}>
              日本語
            </span>
            <button
              onClick={() => setLanguage(language === 'ja' ? 'en' : 'ja')}
              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              style={{ backgroundColor: language === 'en' ? '#3b82f6' : '#9ca3af' }}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  language === 'en' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${language === 'en' ? 'text-blue-600' : 'text-gray-500'}`}>
              English
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <BookOpen className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">{t.title}</h2>
          <p className="text-gray-600">{t.subtitle}</p>
        </div>

        {/* Screenshots Section */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="bg-white rounded-2xl shadow-lg p-4">
            <img
              src="/screenshots/ios_home.png"
              alt="App Home Screen"
              className="w-full rounded-lg border border-gray-200"
            />
            <p className="text-sm text-gray-600 text-center mt-3">
              {language === 'ja' ? 'ホーム画面' : 'Home Screen'}
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-4">
            <img
              src="/screenshots/ios_setting.png"
              alt="App Settings"
              className="w-full rounded-lg border border-gray-200"
            />
            <p className="text-sm text-gray-600 text-center mt-3">
              {language === 'ja' ? '設定画面' : 'Settings Screen'}
            </p>
          </div>
        </div>

        {/* Feature Sections */}
        <div className="space-y-6 mb-12">
          {t.sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div key={index} className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      {section.title}
                    </h3>
                    <p className="text-gray-600 mb-4">{section.description}</p>
                    <ul className="space-y-2">
                      {section.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-50 rounded-full flex items-center justify-center text-blue-600 text-sm font-semibold mt-0.5">
                            {itemIndex + 1}
                          </span>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Member Login Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4">
            <Globe className="w-6 h-6 text-blue-600" />
          </div>
          <p className="text-gray-700 mb-4">{t.memberLogin}</p>
          <button
            onClick={onMemberLogin}
            className="inline-flex items-center gap-2 px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors shadow-lg"
          >
            <Shield className="w-5 h-5" />
            {t.memberLoginButton}
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-600">
          <p>© 2025 SNS Launcher. {language === 'ja' ? 'すべての権利を保有します。' : 'All rights reserved.'}</p>
        </div>
      </div>
    </div>
  );
}
